import { t } from '@transifex/native';

import type { User } from 'pkg/api/models/user';
import { UserSex, MetaField } from 'pkg/api/models/user';
import { getMeta } from 'pkg/api/models/user/utils';
import { ISO3166Alpha2Codes } from 'pkg/countries';
import { yearsSince } from 'pkg/date';

export function fullName(user: User): string {
	if (!user) {
		return '';
	}

	return `${user.firstName} ${user.lastName}`;
}

export function fullNameWithJerseyNumber(
	user: User,
	jerseyNumber?: number
): string {
	if (jerseyNumber === undefined) {
		return fullName(user);
	}

	return `#${jerseyNumber} ${fullName(user)}`;
}

export function initialName(user: User): string {
	if (!user || !user.firstName || !user.lastName) {
		return '';
	}

	const initial = user.firstName.substring(0, 1);

	return `${initial}. ${user.lastName}`;
}

export function initials(user: User): string {
	if (!user || !user.firstName || !user.lastName) {
		return '';
	}

	return `${user.firstName.substring(0, 1)}${user.lastName.substring(0, 1)}`;
}

export function translatedSexString(
	sex: UserSex,
	defaultReturn?: string
): string {
	if (sex === UserSex.Unknown) {
		return t('Unknown');
	} else if (sex === UserSex.Male) {
		return t('Male', { _context: 'male gender for teams' });
	} else if (sex === UserSex.Female) {
		return t('Female', { _context: 'female gender for teams' });
	} else if (sex === UserSex.NotApplicable) {
		return t('Not applicable', { _context: 'no applicable genders for teams' });
	}

	return defaultReturn;
}

export function stringToTranslatedSexString(value: string): string {
	switch (value.toLowerCase()) {
		case 'male':
			return t('Male', { _context: 'male gender for teams' });
		case 'female':
			return t('Female', { _context: 'female gender for teams' });
		case 'notApplicable':
		case 'not_applicable':
			return t('Not applicable', {
				_context: 'no applicable genders for teams',
			});
		default:
			return t('Unknown');
	}
}

export function age(user: User): number {
	return user.birthDate ? yearsSince(new Date(user.birthDate)) : null;
}

export function height(user: User): string {
	return getMeta(user, MetaField.Height);
}

export function formattedAddress(user: User): string {
	if (user.address && user.postalCode && user.city && user.region) {
		return `${user.address}, ${user.postalCode} ${user.city}, ${user.region}`;
	}

	return '';
}

export function getCountryString(user: User): string {
	let countryString = '';

	const countryCode = ISO3166Alpha2Codes().find(
		(c) => c.code === user.country?.toLowerCase()
	)?.country;

	if (user.country && countryCode) {
		countryString = countryCode;
	} else if (user.country) {
		countryString = user.country;
	}

	return countryString;
}

export function joinUserNames(users: User[]): string {
	if (!users || users.length === 0) {
		return '';
	}

	return users.map((user) => fullName(user)).join(', ');
}
